import { useState, useEffect } from "react";
import { useUser } from "../../context/UserContext";
import { UXDataTable } from "../../components/UXDataTable";
import Draft from "../../api/LIMS/Draft";
import { Modal, ModalWrapper } from "./ModalWrapper";
import { ColumnConfig, CsvExportPayload, MenuItem } from "./types";

export const MyDraftCols: ColumnConfig[] = [
  { field: "draftName", headerName: "Draft Name", type: "button-text-draft", path: "/newSample" },
  { field: "draftType", headerName: "Draft Type", type: "label" },
  { field: "draftOwnerEmail", headerName: "Draft Owner", type: "label" },
  { field: "createdDate", headerName: "Create Date", type: "datetime" },
  { field: "lastModifiedDate", headerName: "Last Modified", type: "datetime" },
];

export interface DraftsTableProps {
  onExportPayloadChange: (csvPayLoad: CsvExportPayload) => void;
}

export const DraftsTable = ({ onExportPayloadChange }: DraftsTableProps) => {
  const [draftsIsLoading, setDraftsIsLoading] = useState<boolean>(true);
  const [myDrafts, setMyDrafts] = useState<Draft[] | null>(null);
  const [modal, setModal] = useState<Modal | null>(null);
  const closeModal = () => setModal(null);
  const okButton = { text: "OK", action: closeModal };
  const { username } = useUser();

  const deleteDraft = (draft: Draft) => {
    if (draft && draft.id) {
      Draft.delete(draft.id).then((res) => {
        if (res.message === "Success") {
          setModal({
            title: "Draft Deleted",
            description: "Draft successfully deleted!",
            button: okButton,
          });
          setMyDrafts((prev) => (prev ?? []).filter((d) => d.id !== draft.id));
        } else {
          setModal({
            title: "Draft Failed to Delete",
            description: `${res.message}. Contact support if you feel this is an error.`,
            button: okButton,
          });
        }
      });
    }
  };

  const handleDeleteDraft = (draft: Draft) => {
    if (draft) {
      setModal({
        title: `Delete Draft`,
        description: "Are you sure?",
        button: { text: "Yes", action: () => deleteDraft(draft) },
        secondaryButton: { text: "No", action: closeModal },
      });
    }
  };

  const menuItemsDrafts: MenuItem[] = [
    {
      menuType: "text",
      redirectPath: "",
      text: "Delete Draft",
      onClickFunction: handleDeleteDraft,
    },
  ];

  const prepareExportPayload = () => {
    const csvExport: CsvExportPayload = {
      fileName: "Drafts",
      objList: (myDrafts ?? []).map((draft) => {
        return {
          "Draft Name": draft.draftName,
          "Draft Type": draft.draftType,
          "Draft Owner": draft.draftOwnerEmail,
          "Create Date": draft.createdDate,
          "Last Modified": draft.lastModifiedDate,
        };
      }),
      disabled: draftsIsLoading || myDrafts === null || myDrafts.length === 0,
    };
    return csvExport;
  };

  useEffect(() => {
    let cancelPromise = false;

    if (myDrafts === null) {
      setDraftsIsLoading(true);

      Draft.getMyDrafts("sample").then((res) => {
        if (cancelPromise) return;
        setMyDrafts(res);
        setDraftsIsLoading(false);
      });
    }

    return () => {
      cancelPromise = true;
    };
  }, [username, myDrafts]);

  useEffect(() => {
    const csvPayload = prepareExportPayload();
    onExportPayloadChange(csvPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myDrafts, draftsIsLoading]);

  const noDataDraftsMessage = `No sample drafts found for owner ${username}`;

  return (
    <>
      <UXDataTable
        tableWidth="100%"
        cols={MyDraftCols}
        rows={myDrafts === null ? [] : myDrafts.sort((a, b) => b.draftName - a.draftName)}
        moreOptionsCell={true}
        enablePaging={true}
        noDataMessage={noDataDraftsMessage}
        menuProps={menuItemsDrafts}
        defaultRowsPerPage={10}
        isDataLoading={draftsIsLoading}
        tableName={"sampleDrafts"}
        enableSorting={true}
        dataRefreshTrigger={undefined}
        setDataRefreshTrigger={undefined}
      />
      {modal !== null && <ModalWrapper modal={modal} />}
    </>
  );
};
